import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowUpRight, Calendar as CalendarIcon, Clock, Layout, MapPin, User, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { cn } from '../../lib/utils';
import { getStatusConfig } from '../../utils/dashboard.utils';
import { LoadingSpinner } from '../ui/LoadingSpinner';

export function CalendarEventsTable() {
  const { events, isLoading, error } = useCalendarEvents();

  // Filtrer pour n'afficher que les événements à venir ou récents (max 5)
  const displayEvents = [...events].sort((a, b) => b.start.getTime() - a.start.getTime()).slice(0, 5);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mt-6">
      <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-[#00758d]" />
          Événements & Occupations
        </h3>
        <Link
          to="/calendar"
          className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
        >
          Voir calendrier complet
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-[#f8fafc] text-slate-500 border-b border-slate-200 font-medium whitespace-nowrap">
            <tr>
              <th className="px-5 py-3.5 pl-6">Type & Titre</th>
              <th className="px-5 py-3.5">Date & Heure</th>
              <th className="px-5 py-3.5">Espace</th>
              <th className="px-5 py-3.5">Organisateur / Client</th>
              <th className="px-5 py-3.5 pr-6 text-right">Statut / Réf</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center text-slate-400">
                  <LoadingSpinner message="Chargement des événements..." submessage="" size="sm" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center text-red-500">
                  <p>{error}</p>
                </td>
              </tr>
            ) : displayEvents.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center text-slate-400">
                  <p>Aucun événement planifié.</p>
                </td>
              </tr>
            ) : (
              displayEvents.map((event) => (
                <tr key={event.id} className="hover:bg-slate-50/80 transition-colors group">
                  <td className="px-5 py-3.5 pl-6 align-middle">
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          'p-2 rounded-lg flex-shrink-0',
                          event.type === 'calendar_event' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600',
                        )}
                      >
                        {event.type === 'calendar_event' ? <Layout size={16} /> : <Zap size={16} />}
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="font-bold text-slate-900 truncate max-w-[200px]" title={event.title}>
                          {event.title}
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-widest opacity-50">
                          {event.type === 'calendar_event' ? 'Événement SING' : 'Réservation Client'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 align-middle">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1.5 font-medium text-slate-700">
                        <CalendarIcon size={12} className="text-slate-400" />
                        {format(event.start, 'dd MMM yyyy', { locale: fr })}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
                        <Clock size={12} />
                        {event.allDay
                          ? 'Journée entière'
                          : `${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 align-middle">
                    <div className="flex items-center gap-1.5">
                      <MapPin size={12} className="text-[#00758d]" />
                      <span className="font-medium text-slate-600">{event.spaceName || 'N/A'}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 align-middle">
                    <div className="flex items-center gap-1.5">
                      <User size={12} className="text-slate-400" />
                      <span className="text-slate-600 truncate max-w-[150px]">
                        {event.organizerName || event.customerName || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 pr-6 text-right align-middle">
                    <div className="flex flex-col items-end gap-1">
                      {event.type === 'booking' ? (
                        <>
                          {event.bookingStatus && (
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-white/20',
                                getStatusConfig(event.bookingStatus).bg,
                                getStatusConfig(event.bookingStatus).text,
                              )}
                            >
                              {getStatusConfig(event.bookingStatus).label}
                            </span>
                          )}
                          <Link
                            to={`/bookings/${event.id}`}
                            className="text-[10px] font-black text-primary hover:underline"
                          >
                            {event.reference || 'VOIR DOSSIER'}
                          </Link>
                        </>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 uppercase tracking-tighter">
                          Événement
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
